import React, { useEffect, useState } from 'react'
import { db, storage } from '../../firebaseConfig'
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  onSnapshot,
  getDocs,
  Timestamp,
  doc,       // Tambahkan ini
  deleteDoc, // Tambahkan ini
  } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { 
  ArrowLeft, 
  ImagePlus, 
  Send, 
  Calendar, 
  Clock,    
  Repeat,
  Trash2,
  FileText
} from 'lucide-react'

// 🔹 DATA TEMPLATE SIAP PAKAI
const PREDEFINED_TEMPLATES = [
  {
    label: "Selamat Datang",
    title: "Selamat Datang di Zimo Mitra! 🎉",
    message: "Halo Mitra Hebat! Selamat bergabung di Zimo Mitra. Segera aktifkan aplikasimu setiap hari, ikuti panduan, dan raih pendapatan maksimal."
  },
  {
    label: "Program Referral",
    title: "Buka Keran Cuan dengan Kode Referral! 💸",
    message: "Ajak rekan seprofesi bergabung menggunakan Kode Referral milikmu. Semakin banyak yang daftar dan aktif pakai kodemu, semakin besar peluang penghasilan tambahanmu!"
  },
  {
    label: "Waktu Ramai",
    title: "Waktu Ramai Orderan Tiba! 🔥",
    message: "Perhatian! Saat ini memasuki jam sibuk dengan tingkat pesanan tinggi. Pastikan aplikasimu aktif dan bersiaplah menerima orderan untuk memaksimalkan penghasilan harianmu."
  },
  {
    label: "Area Ramai",
    title: "Area Ini Sedang Ramai Pesanan! 📍",
    message: "Banyak pelanggan mencari layanan di area [NAMA AREA/KOTA] saat ini! Geser ke area tersebut atau aktifkan aplikasimu sekarang untuk peluang mendapat orderan lebih cepat."
  },
  {
    label: "Tolak/Abaikan Order",
    title: "Peringatan Performa: Tolak & Abaikan Order ⚠️",
    message: "Mohon diperhatikan! Terlalu sering mengabaikan atau menolak orderan yang masuk dapat menurunkan performa akun dan mengurangi peluangmu mendapatkan orderan di masa depan. Mari jaga performa!"
  }
];

/**
 * NotifTerapisPage (FINAL VERSION)
 * -------------------------------------
 * Fitur:
 * 1. Kirim notifikasi ke ID Mitra (ZT-xxx) atau Broadcast (all_terapis).
 * 2. Penjadwalan (Scheduled) & Pengulangan (Recurring).
 * 3. Upload Gambar.
 */

export default function NotifTerapisPage({ onBack }) {
  const [notifications, setNotifications] = useState([])
  
  // State Form
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  
  // 🔹 State untuk menampung banyak penerima (Array)
  const [selectedTargets, setSelectedTargets] = useState(['all_terapis']) 
  
  const [sending, setSending] = useState(false)
  
  // State Gambar
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  
  // State Data Terapis
  const [terapisList, setTerapisList] = useState([])
  const [loadingTerapis, setLoadingTerapis] = useState(true)

  // State Jadwal
  const [scheduledDate, setScheduledDate] = useState('') 
  const [recurrence, setRecurrence] = useState('none') 
  const [deleteDate, setDeleteDate] = useState('') // 🔹 Tambahan state hapus otomatis 

  // 1. Load Notifikasi (Realtime)
  useEffect(() => {
    const q = query(
      collection(db, 'notifications'),
      orderBy('createdAt', 'desc')
    )
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      setNotifications(data)
    })
    return () => unsub()
  }, [])

  // 🔹 FUNGSI BARU: Pengecekan Hapus Otomatis setiap 1 menit
  useEffect(() => {
    const checkAndDelete = async () => {
      const now = new Date()
      notifications.forEach(async (n) => {
        if (n.deleteAt && n.deleteAt.toDate() <= now) {
          try {
            await deleteDoc(doc(db, 'notifications', n.id))
            if (n.imageUrl) {
              await deleteObject(ref(storage, n.imageUrl))
            }
          } catch (err) {
            console.error('Gagal auto-delete:', err)
          }
        }
      })
    }
    
    // Cek saat pertama load dan setiap 60 detik
    checkAndDelete()
    const interval = setInterval(checkAndDelete, 60000)
    return () => clearInterval(interval)
  }, [notifications])

  // 2. Load Terapis (Ambil ID Number untuk Value Dropdown)
  useEffect(() => {
    let mounted = true
    const fetchTerapis = async () => {
      try {
        const snap = await getDocs(collection(db, 'therapists'))
        const list = snap.docs.map((d) => {
          const data = d.data() || {}
          
          // Pastikan kita punya ID Number (Fallback ke ZT-UID jika kosong)
          const idNumber =
            data.id_number && typeof data.id_number === 'string'
              ? data.id_number
              : `ZT-${d.id.slice(-5)}` 
              
          const displayName = data.fullName || data.name || idNumber
          
          // Kita simpan idNumber sebagai value utama, UID hanya sebagai key
          return { uid: d.id, idNumber, displayName }
        })
        if (mounted) {
          list.sort((a, b) => a.displayName.localeCompare(b.displayName))
          setTerapisList(list)
        }
      } catch (err) {
        console.error('Gagal memuat daftar terapis:', err)
      } finally {
        if (mounted) setLoadingTerapis(false)
      }
    }
    fetchTerapis()
    return () => {
      mounted = false
    }
  }, [])

  // 3. Fungsi Kirim
  const sendNotification = async () => {
    if (!title.trim() || !message.trim()) {
      alert('Judul dan pesan tidak boleh kosong.')
      return
    }

    setSending(true)
    let imageUrl = null

    try {
      // Upload Gambar
      if (imageFile) {
        const storageRef = ref(
          storage,
          `notifications_photos/${Date.now()}_${imageFile.name}`
        )
        await uploadBytes(storageRef, imageFile)
        imageUrl = await getDownloadURL(storageRef)
      }

      // Cek Jadwal
      let scheduledTimestamp = serverTimestamp()
      let status = 'sent' 
      
      // 🔹 Cek Waktu Hapus
      let deleteTimestamp = null
      if (deleteDate) {
        deleteTimestamp = Timestamp.fromDate(new Date(deleteDate))
      }
      
      if (scheduledDate) {
        const dateObj = new Date(scheduledDate)
        scheduledTimestamp = Timestamp.fromDate(dateObj)
        status = 'scheduled'
      }

      // 🔹 FINAL TARGET: Menggabungkan array menjadi string agar mudah dibaca di riwayat
      const finalTarget = selectedTargets.join(', ');

      const payload = {
        title: title.trim(),
        message: message.trim(),
        sender: 'Admin Zimo',
        target: finalTarget, // Sekarang bisa berisi "ZT-001, ZT-002, ..."
        imageUrl: imageUrl || '',
        
        createdAt: serverTimestamp(), 
        scheduledAt: scheduledTimestamp,
        
        status: status,
        isRecurring: recurrence !== 'none',
        frequency: recurrence,
        deleteAt: deleteTimestamp, // 🔹 Tambahkan ke payload
      }

      await addDoc(collection(db, 'notifications'), payload)

      const successMsg = scheduledDate 
        ? '✅ Notifikasi berhasil dijadwalkan!' 
        : '✅ Notifikasi berhasil dikirim!'
      
      alert(successMsg)
      
      // Reset Form
      setTitle('')
      setMessage('')
      setImageFile(null)
      setImagePreview(null)
      setSelectedTargets(['all_terapis']) // 🔹 PERBAIKAN: Gunakan setSelectedTargets dengan array
      setScheduledDate('') 
      setRecurrence('none') 
      setDeleteDate('') // 🔹 Reset state hapus

    } catch (err) {
      console.error('Gagal mengirim notifikasi:', err)
      alert('❌ Gagal memproses notifikasi.')
    } finally {
      setSending(false)
    }
  }
  // Tambahkan fungsi ini
const deleteNotification = async (id, imageUrl) => {
  if (window.confirm('Apakah Anda yakin ingin menghapus notifikasi beserta gambarnya?')) {
    try {
      // 1. Hapus dokumen data dari Firestore
      await deleteDoc(doc(db, 'notifications', id));

      // 2. Jika notifikasi memiliki gambar, hapus juga file fisiknya dari Storage
      if (imageUrl) {
        const imageRef = ref(storage, imageUrl);
        await deleteObject(imageRef);
      }

    } catch (err) {
      console.error('Gagal menghapus:', err);
      alert('❌ Gagal menghapus notifikasi.');
    }
  }
}

  // 🔹 Fungsi untuk menerapkan template ke dalam form
  const handleApplyTemplate = (template) => {
    setTitle(template.title)
    setMessage(template.message)
  }

  // 🔹 Fungsi untuk menambah/menghapus ID dari daftar pilihan
  const toggleTarget = (id) => {
    setSelectedTargets((prev) => {
      // Jika pilih "Broadcast", hapus semua pilihan individu
      if (id === 'all_terapis' || id === 'all') return [id];
      
      // Jika sebelumnya "Broadcast", lalu pilih individu, hapus "Broadcast"-nya
      const filtered = prev.filter(item => item !== 'all_terapis' && item !== 'all');
      
      if (filtered.includes(id)) {
        const newSelection = filtered.filter((i) => i !== id);
        return newSelection.length === 0 ? ['all_terapis'] : newSelection;
      } else {
        return [...filtered, id];
      }
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setImageFile(file)
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result)
    }
    reader.readAsDataURL(file)
  }

  return (
    <div className="text-gray-200">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 text-gray-300"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-100">
              Notifikasi Terapis
            </h2>
            <p className="text-sm text-gray-500">
              Kirim pengumuman ke ID Mitra atau Broadcast.
            </p>
          </div>
        </div>
      </div>

      {/* FORM AREA */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-6">
        
        {/* 🔹 Bagian Tombol Template Siap Pakai */}
        <div className="mb-4 border-b border-gray-800 pb-4">
          <div className="flex items-center gap-2 mb-2">
             <FileText size={16} className="text-green-400"/>
             <span className="text-sm font-semibold text-gray-300">Pilih Template Cepat</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {PREDEFINED_TEMPLATES.map((tmpl, idx) => (
              <button
                key={idx}
                onClick={() => handleApplyTemplate(tmpl)}
                className="px-3 py-1.5 text-xs bg-gray-800 border border-gray-700 hover:bg-gray-700 hover:border-gray-500 text-gray-300 rounded-full transition-colors"
              >
                {tmpl.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <input
            type="text"
            placeholder="Judul Notifikasi"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="md:col-span-3 bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:ring-1 focus:ring-gray-700"
          />

          <div className="md:col-span-3 bg-gray-950 border border-gray-800 rounded-lg p-2 max-h-40 overflow-y-auto">
            <p className="text-xs text-gray-500 mb-2 px-2">Pilih Penerima (Bisa pilih lebih dari satu):</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
              {/* Opsi Broadcast */}
              <label className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-900 ${selectedTargets.includes('all_terapis') ? 'bg-blue-900/30' : ''}`}>
                <input type="checkbox" checked={selectedTargets.includes('all_terapis')} onChange={() => toggleTarget('all_terapis')} className="rounded border-gray-700" />
                <span className="text-sm">Semua Terapis (Broadcast)</span>
              </label>

              {loadingTerapis ? (
                <p className="text-xs p-2">Memuat daftar...</p>
              ) : (
                terapisList.map((t) => (
                  <label key={t.uid} className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-900 ${selectedTargets.includes(t.idNumber) ? 'bg-blue-900/30' : ''}`}>
                    <input type="checkbox" checked={selectedTargets.includes(t.idNumber)} onChange={() => toggleTarget(t.idNumber)} className="rounded border-gray-700" />
                    <span className="text-sm text-gray-300">{t.idNumber} - {t.displayName}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>

        <textarea
          rows={4}
          placeholder="Tulis pesan..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-700 mb-3"
        />

        {/* --- Bagian Jadwal --- */}
        <div className="bg-gray-800/50 p-3 rounded-lg mb-4 border border-gray-800">
          <div className="flex items-center gap-2 mb-3">
             <Clock size={16} className="text-blue-400"/>
             <span className="text-sm font-semibold text-gray-300">Pengaturan Jadwal (Opsional)</span>
          </div>
          
          {/* 🔹 Ubah grid-cols-2 jadi grid-cols-3 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Waktu Pengiriman</label>
              <div className="relative">
                <input
                  type="datetime-local"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 focus:ring-1 focus:ring-blue-600"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Ulangi Otomatis</label>
              <div className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2">
                <Repeat size={16} className="text-gray-400" />
                <select 
                  value={recurrence}
                  onChange={(e) => setRecurrence(e.target.value)}
                  className="bg-transparent text-sm text-gray-200 w-full outline-none"
                >
                  <option value="none">Tidak Berulang</option>
                  <option value="daily">Setiap Hari</option>
                  <option value="weekly">Setiap Minggu</option>
                  <option value="monthly">Setiap Bulan</option>
                </select>
              </div>
            </div>
            
            {/* 🔹 Input Baru: Waktu Hapus Otomatis */}
            <div>
              <label className="text-xs text-red-400 mb-1 block">Hapus Otomatis (Opsional)</label>
              <div className="relative">
                <input
                  type="datetime-local"
                  value={deleteDate}
                  onChange={(e) => setDeleteDate(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 focus:ring-1 focus:ring-red-600"
                />
              </div>
            </div>
          </div>
        </div>

        {/* --- Bagian Gambar --- */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <label
              htmlFor="notifImage"
              className="flex items-center gap-2 cursor-pointer bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded-lg text-sm text-gray-200 transition"
            >
              <ImagePlus size={16} />
              {imageFile ? 'Ganti Gambar' : 'Tambah Gambar'}
            </label>
            <input
              type="file"
              id="notifImage"
              accept="image/*"
              className="hidden"
              onChange={handleImageChange}
            />
            {imageFile && (
              <span className="text-xs text-gray-400 truncate max-w-[150px]">{imageFile.name}</span>
            )}
          </div>
          {imagePreview && (
            <img
              src={imagePreview}
              alt="Preview"
              className="w-20 h-20 object-cover rounded-lg border border-gray-700"
            />
          )}
        </div>

        {/* --- Tombol Kirim --- */}
        <div className="flex justify-end">
          <button
            onClick={sendNotification}
            disabled={sending}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-white text-sm font-medium transition ${
              sending 
                ? 'bg-gray-700 cursor-not-allowed' 
                : scheduledDate 
                  ? 'bg-orange-600 hover:bg-orange-700' 
                  : 'bg-blue-600 hover:bg-blue-700'     
            }`}
          >
            {sending ? 'Memproses...' : scheduledDate ? 'Jadwalkan' : 'Kirim Sekarang'}
          </button>
        </div>
      </div>

      {/* LIST RIWAYAT NOTIFIKASI */}
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-gray-200">Riwayat & Jadwal</h3>
        {notifications.length === 0 ? (
          <p className="text-gray-500 text-sm">Belum ada notifikasi.</p>
        ) : (
          notifications.map((n) => {
             const isScheduled = n.status === 'scheduled';
             const isRecurring = n.isRecurring === true;
             
             return (
              <div key={n.id} className={`border rounded-xl p-4 relative ${
                  isScheduled ? 'bg-gray-900 border-orange-500/30' : 'bg-gray-900 border-gray-800'
                }`}>
                
                {/* Badge Status */}
                <div className="absolute top-4 right-4 flex gap-2">
                  {isScheduled && (
                    <span className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded border border-orange-500/30">
                      Terjadwal
                    </span>
                  )}
                  {isRecurring && (
                    <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded border border-purple-500/30">
                      {n.frequency}
                    </span>
                  )}
                </div>

                {n.imageUrl && n.imageUrl.startsWith('http') && (
                  <img
                    src={n.imageUrl}
                    alt="Notifikasi"
                    className="rounded-lg mb-3 max-h-[200px] object-cover"
                  />
                )}

                <h3 className="font-semibold text-gray-100 pr-20">{n.title}</h3>
                <p className="text-sm text-gray-400">{n.message}</p>
                
                <div className="flex justify-between items-end mt-2 pt-2 border-t border-gray-800">
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                    <span>Target: <span className="text-blue-400">{n.target}</span></span>
    
                    {isScheduled ? (
                      <span className="text-orange-400 flex items-center gap-1">
                        <Clock size={12}/> 
                        Akan dikirim: {n.scheduledAt?.toDate ? n.scheduledAt.toDate().toLocaleString('id-ID') : '-'}
                      </span>
                    ) : (
                      <span>
                        Dikirim: {n.createdAt?.toDate ? n.createdAt.toDate().toLocaleString('id-ID') : 'Baru saja'}
                      </span>
                    )}
                    
                    {/* 🔹 Tampilkan info waktu hapus jika diatur */}
                    {n.deleteAt && n.deleteAt.toDate && (
                      <span className="text-red-400 flex items-center gap-1 ml-3 border-l border-gray-700 pl-3">
                        <Trash2 size={12}/> 
                        Berakhir: {n.deleteAt.toDate().toLocaleString('id-ID')}
                      </span>
                    )}
                  </div>

                  {/* TOMBOL HAPUS BARU */}
                  <button 
                    onClick={() => deleteNotification(n.id, n.imageUrl)}
                    className="p-1.5 text-gray-500 hover:text-red-500 transition-colors"
                    title="Hapus"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
